import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { 
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock,
  FaPaperPlane, FaUser, FaComment, FaGraduationCap,
  FaUniversity, FaUserTie, FaCalendarAlt, FaGlobeAfrica
} from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: 'Email Us',
      details: ['contact@tutorconnectcm.com', 'support@tutorconnectcm.com'],
      link: 'mailto:contact@tutorconnectcm.com',
      color: 'var(--secondary-500)'
    },
    {
      icon: <FaPhone />,
      title: 'Call Us',
      details: ['+237 671 657 357', '+237 674 464 389'],
      link: 'tel:+237671657357',
      color: 'var(--primary-500)'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Location',
      details: ['University of Buea', 'College of Technology', 'Buea, Cameroon'],
      link: 'https://maps.google.com/?q=University+of+Buea',
      color: 'var(--accent-500)'
    },
    {
      icon: <FaClock />,
      title: 'Available Hours',
      details: ['Mon - Fri: 8:00 AM - 6:00 PM', 'Saturday: 9:00 AM - 1:00 PM'],
      link: null,
      color: 'var(--info-500)'
    }
  ];

  const projectDetails = [
    { icon: <FaGraduationCap />, label: 'Course', value: 'CEC315 - Cloud Computing' },
    { icon: <FaUniversity />, label: 'Institution', value: 'University of Buea' },
    { icon: <FaGlobeAfrica />, label: 'Department', value: 'College of Technology' },
    { icon: <FaUserTie />, label: 'Team Leader', value: 'Besong Wisdom' },
    { icon: <FaCalendarAlt />, label: 'Academic Year', value: '2025/2026' },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSuccess('Thank you for your message! We\'ll respond within 24 hours.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <h1>Get in Touch</h1>
            <p className="hero-subtitle">
              Connect with the Tutor Connect Team
            </p>
            <p className="hero-description">
              Have questions about our platform, need support, or want to collaborate? 
              We're here to help. Reach out to us through any channel below.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-wrapper">
            {/* Left Column - Contact Info */}
            <div className="contact-info-wrapper">
              <div className="contact-info-card">
                <h2>Contact Information</h2>
                <p className="contact-intro">
                  Feel free to reach out to us for any inquiries about our cloud computing project.
                </p>
                
                <div className="contact-methods">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="contact-method">
                      <div 
                        className="method-icon"
                        style={{ backgroundColor: info.color }}
                      >
                        {info.icon}
                      </div>
                      <div className="method-content">
                        <h3>{info.title}</h3>
                        <div className="method-details">
                          {info.details.map((detail, idx) => (
                            <p key={idx}>{detail}</p>
                          ))}
                        </div>
                        {info.link && (
                          <a 
                            href={info.link} 
                            className="method-action"
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {info.title === 'Email Us' ? 'Send Email' : 
                             info.title === 'Call Us' ? 'Make Call' : 
                             'View Location'}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Project Info Box */}
                <div className="project-info-box">
                  <div className="project-info-header">
                    <FaGraduationCap className="project-icon" />
                    <h3>Academic Project</h3>
                  </div>
                  <div className="project-details-list">
                    {projectDetails.map((detail, index) => (
                      <div key={index} className="project-detail-item">
                        <div className="detail-icon">
                          {detail.icon}
                        </div>
                        <div className="detail-content">
                          <span className="detail-label">{detail.label}</span>
                          <span className="detail-value">{detail.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="contact-form-wrapper">
              <div className="form-container">
                <div className="form-header">
                  <h2>Send us a Message</h2>
                  <p>Fill out the form and we'll get back to you promptly</p>
                </div>

                {success && (
                  <div className="success-message">
                    <FaPaperPlane className="success-icon" />
                    <div className="success-content">
                      <h4>Message Sent Successfully!</h4>
                      <p>{success}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FaUser className="input-icon" />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <FaEnvelope className="input-icon" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <FaComment className="input-icon" />
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="form-select"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="academic">Academic Collaboration</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Your Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Type your message here..."
                      rows="6"
                      required
                      disabled={loading}
                      className="form-textarea"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="btn-icon" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="quick-info">
        <div className="container">
          <div className="quick-info-grid">
            <div className="info-card">
              <div className="info-icon" style={{ backgroundColor: 'var(--primary-100)' }}>
                <FaClock style={{ color: 'var(--primary-600)' }} />
              </div>
              <h3>Response Time</h3>
              <p>We typically respond to all inquiries within 24 hours during business days.</p>
            </div>

            <div className="info-card">
              <div className="info-icon" style={{ backgroundColor: 'var(--secondary-100)' }}>
                <FaUserTie style={{ color: 'var(--secondary-600)' }} />
              </div>
              <h3>Project Team</h3>
              <p>This is an academic project by Group 9, supervised by Mr. Kometa.</p>
            </div>

            <div className="info-card">
              <div className="info-icon" style={{ backgroundColor: 'var(--accent-100)' }}>
                <FaGlobeAfrica style={{ color: 'var(--accent-600)' }} />
              </div>
              <h3>Made for Cameroon</h3>
              <p>Specifically designed to address educational challenges in Cameroon.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Contact;